import { useEffect, useState } from "react";
import { useUserId } from "@/features/auth/hooks/useUserId";
import { useLocation } from "@/hooks/useLocation";
import { isWebView } from "utilities/display";
import { useContentCardsSubscription } from "../hooks/useContentCardsSubscription";
import { showCustomIAM } from "../iam/ShowCustomIAM";

export const BrazeInitializer = () => {
  const [isBrazeReady, setIsBrazeReady] = useState(false);
  const userId = useUserId();
  const { pathname } = useLocation();
  const isAppWebView = isWebView();

  useEffect(() => {
    if (isAppWebView) {
      return;
    }

    // window.analytics is assigned synchronously and buffers anything called
    // before Segment loads, so this can be registered straight away. Braze
    // rides in as a device-mode destination; ready() still fires when that
    // bundle is blocked, so its absence is an end state, not a "not yet".
    window.analytics?.ready(() => {
      if (window.braze) {
        setIsBrazeReady(true);
      }
    });
  }, [isAppWebView]);

  // Braze only replays a session's cards and messages to subscribers that
  // registered before openSession, so this has to stay above the effect below.
  useContentCardsSubscription(isBrazeReady);

  useEffect(() => {
    const braze = window.braze;

    if (!isBrazeReady || !braze) {
      return;
    }

    const subscription = braze.subscribeToInAppMessage((inAppMessage) => {
      if (inAppMessage instanceof braze.SlideUpMessage) {
        braze.logInAppMessageImpression(inAppMessage);
        showCustomIAM(inAppMessage);
      } else {
        braze.showInAppMessage(inAppMessage);
      }
    });

    if (braze.isPushSupported() && !braze.isPushPermissionGranted()) {
      braze.requestPushPermission();
    }

    braze.openSession();

    return () => {
      if (subscription) {
        braze.removeSubscription(subscription);
      }
    };
  }, [isBrazeReady]);

  useEffect(() => {
    if (!isBrazeReady) {
      return;
    }

    window.braze?.requestContentCardsRefresh();
  }, [isBrazeReady, userId, pathname]);

  return null;
};
